import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { ChartOrderItem, DataType, SessionDistributionType } from '../../utils/types/analytics/analytics_types';
import { MediumColors } from './Colors';
import { DashletIconGroup } from './DashletIconGroup';

type ColumnChartBlueProps = {
  id: string;
  data: Array<DataType>;
  title: string;
  dashletItem: ChartOrderItem;
  onDragStart: (item: ChartOrderItem) => void;
  onDragEnter: (item: ChartOrderItem) => void;
  onDragOver: (event: React.DragEvent<HTMLDivElement>) => void;
  onDrop: (event: React.DragEvent<HTMLDivElement>) => void;
  fullScreenDetected: boolean;
  setFullScreenDetected: (fullscreen: boolean) => void;
  handleDashletVisibility: (item: ChartOrderItem) => void;
  editMode: boolean;
};

export const ColumnChartBlue = ({
  id,
  data,
  title,
  dashletItem,
  onDragStart,
  onDragEnter,
  onDragOver,
  onDrop,
  fullScreenDetected,
  setFullScreenDetected,
  handleDashletVisibility,
  editMode,
}: ColumnChartBlueProps) => {
  const [dataVals, setDataVals] = useState<{ [key: string]: any }[]>([]);

  useEffect(() => {
    formatData(data);
  }, [data, title]);

  const formatData = (data: any) => {
    if (data !== undefined) {
      const val = data.map((item: SessionDistributionType) => ({
        name: `${item.day}-${new Date(item.year, item.month - 1, item.day).toLocaleString('default', { month: 'short' })}-${item.year}`,
        value: item.count,
      }));
      setDataVals(val);
    }
  };

  const handleDragStart = () => {
    if (onDragStart) {
      onDragStart(dashletItem);
    }
  };

  const handleDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (onDragEnter) {
      onDragEnter(dashletItem);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (onDragOver) {
      onDragOver(event);
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (onDrop) {
      onDrop(event);
    }
  };

  return (
    <Paper
      variant="outlined"
      className="group"
      sx={{
        position: 'relative',
        width: '100%',
        gridColumn: { xs: 'span 1', lg: 'span 2' },
        borderRadius: '16px',
        height: '100%',
        p: 2.5,
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '&:hover': {
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.06)',
          borderColor: 'primary.main',
        },
      }}
    >
      <DashletIconGroup
        id={id}
        dashletItem={dashletItem}
        setFullScreenDetected={setFullScreenDetected}
        handleDashletVisibility={handleDashletVisibility}
        editMode={editMode}
      />

      <Box
        id={id}
        draggable
        onDragStart={handleDragStart}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        sx={{
          width: '100%',
          height: '100%',
          opacity: dashletItem.hidden ? 0.4 : 1,
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: fullScreenDetected ? '1.5rem' : '0.875rem',
            color: 'text.secondary',
            mb: 2,
            textAlign: 'center',
          }}
        >
          {title}
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
          {!data || data.length === 0 ? (
            <Box
              sx={{
                height: 300,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'text.secondary',
                fontSize: '0.875rem',
              }}
            >
              No data to preview
            </Box>
          ) : (
            <ResponsiveContainer width="100%" height={fullScreenDetected ? 500 : 300}>
              <BarChart
                data={dataVals}
                margin={{ top: 10, right: 30, left: 20, bottom: 40 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="name"
                  scale="auto"
                  angle={-45}
                  textAnchor="end"
                  interval={0}
                  fontSize={10}
                />
                <YAxis />
                <Tooltip />
                <Bar dataKey="value" fill={MediumColors.BLUE} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Box>
      </Box>
    </Paper>
  );
};
